import {
  Box,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  useBreakpointValue,
  type FlexProps,
} from "@chakra-ui/react";
import { forwardRef } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ContractOverviewFigure,
  type ContractExpiryMonthDto,
  type ContractOverviewDto,
  type ContractReportQueryDto,
} from "../../autogen/bff-api";
import { displaySum } from "../../common/formatting/displaySum";
import { formatNumber } from "../../common/formatting/formatNumber";
import { momentLocaleFor } from "../../common/formatting/i18nFormatters";
import { registerLink } from "./contractFigures";
import { SeeTheListButton } from "./SeeTheListButton";
import {
  InPageDetails,
  useIsTouchScreen,
  useTouchDetails,
} from "./TouchDetails";

const BAR_HEIGHT = 140;

export const ContractExpiryTimeline = ({
  overview,
  base,
}: {
  overview: ContractOverviewDto;
  base: ContractReportQueryDto;
}) => {
  const { t } = useTranslation();
  const tallest = Math.max(
    1,
    ...overview.expiryTimeline.map(
      (month) => month.withOption + month.withoutOption,
    ),
  );
  const nothingExpires =
    tallest === 1 &&
    overview.expiryTimeline.every(
      (month) => month.withOption + month.withoutOption === 0,
    );
  const activeContracts =
    overview.figures.find(
      (figure) => figure.figure === ContractOverviewFigure.Active,
    )?.value ?? 0;
  const heading = t("Contracts expiring over the next 12 months");
  const compact =
    useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false;
  const isTouch = useIsTouchScreen();
  const { regionRef, openKey, triggerProps, detailsId } =
    useTouchDetails<string>();
  const openMonth = overview.expiryTimeline.find(
    (month) => month.month === openKey,
  );
  return (
    <Stack
      spacing="3"
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="lg"
      p="4"
      bg="smBackground"
    >
      <Text as="h2" fontWeight="semibold" color="smText">
        {heading}
      </Text>
      {nothingExpires ? (
        <Text fontSize="sm" color="smMuted" data-testid="expiry-timeline-empty">
          {t("No contract expires in the next 12 months.")}
        </Text>
      ) : (
        <Stack spacing="3" ref={regionRef}>
          <Flex gap="3" fontSize="xs" color="smMuted" wrap="wrap">
            <Legend color="smChartSeries1" label={t("Has an option")} />
            <Legend color="smChartSeries2" label={t("No option")} />
          </Flex>
          <Box overflowX={compact ? "visible" : "auto"}>
            <Flex
              align="flex-end"
              gap={compact ? "0.5" : "1"}
              minW={compact ? undefined : "36rem"}
              h={`${BAR_HEIGHT + (compact ? 40 : 24)}px`}
              data-testid="expiry-timeline-bars"
            >
              {overview.expiryTimeline.map((month, index) =>
                isTouch ? (
                  <MonthBar
                    key={month.month}
                    month={month}
                    tallest={tallest}
                    compact={compact}
                    firstMonth={index === 0}
                    _expanded={{ bg: "smPrimarySofter" }}
                    {...triggerProps(month.month)}
                  />
                ) : (
                  <HoverMonthBar
                    key={month.month}
                    month={month}
                    tallest={tallest}
                    currency={overview.annualValueCurrency}
                    link={registerLink(base, month)}
                    compact={compact}
                    firstMonth={index === 0}
                  />
                ),
              )}
            </Flex>
          </Box>
          {isTouch && openMonth && (
            <InPageDetails
              id={detailsId}
              data-testid={`expiry-month-details-${openMonth.month}`}
            >
              <MonthDetails
                month={openMonth}
                currency={overview.annualValueCurrency}
                link={registerLink(base, openMonth)}
              />
            </InPageDetails>
          )}
        </Stack>
      )}
      {overview.activeWithoutEndDate > 0 && (
        <Text fontSize="sm" color="smMuted">
          {base.activeFrameworkAgreement
            ? t(
                "{{missing}} of {{total}} suppliers in active framework agreements have no end date and are not included in «{{section}}».",
                {
                  missing: formatNumber(overview.activeWithoutEndDate),
                  total: formatNumber(activeContracts),
                  section: heading,
                },
              )
            : t(
                "{{missing}} of {{total}} active contracts have no end date and are not included in «{{section}}».",
                {
                  missing: formatNumber(overview.activeWithoutEndDate),
                  total: formatNumber(activeContracts),
                  section: heading,
                },
              )}
        </Text>
      )}
    </Stack>
  );
};

const Legend = ({ color, label }: { color: string; label: string }) => (
  <Flex align="center" gap="1">
    <Box w="3" h="3" borderRadius="sm" bg={color} />
    <Text as="span">{label}</Text>
  </Flex>
);

const HoverMonthBar = ({
  currency,
  link,
  ...bar
}: MonthBarProps & { currency: string; link: string }) => {
  const navigate = useNavigate();
  const total = bar.month.withOption + bar.month.withoutOption;
  return (
    <Popover trigger="hover" placement="top" isLazy openDelay={150}>
      <PopoverTrigger>
        <MonthBar
          {...bar}
          onClick={total === 0 ? undefined : () => navigate(link)}
        />
      </PopoverTrigger>
      <PopoverContent
        maxW="xs"
        bg="smBackground"
        borderColor="smBorder"
        data-testid={`expiry-month-details-${bar.month.month}`}
      >
        <PopoverArrow bg="smBackground" />
        <PopoverBody fontSize="sm">
          <MonthDetails month={bar.month} currency={currency} link={link} />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

type MonthBarProps = {
  month: ContractExpiryMonthDto;
  tallest: number;
  compact: boolean;
  firstMonth: boolean;
};

const useMonthStart = (month: ContractExpiryMonthDto) => {
  const { i18n } = useTranslation();
  return moment(`${month.month}-01`).locale(momentLocaleFor(i18n.language));
};

const MonthBar = forwardRef<HTMLButtonElement, FlexProps & MonthBarProps>(
  function MonthBar({ month, tallest, compact, firstMonth, ...rest }, ref) {
    const { t } = useTranslation();
    const total = month.withOption + month.withoutOption;
    const monthStart = useMonthStart(month);
    const label = monthStart.format(compact ? "MMM" : "MMM YY");
    const yearLabel =
      firstMonth || monthStart.month() === 0 ? monthStart.format("YYYY") : "";
    const height = (count: number) =>
      `${Math.round((count / tallest) * BAR_HEIGHT)}px`;

    return (
      <Flex
        ref={ref}
        as="button"
        type="button"
        direction="column"
        align="stretch"
        justify="flex-end"
        flex="1"
        minW="0"
        h="full"
        borderRadius="sm"
        _hover={{ bg: "smPrimarySofter" }}
        _focusVisible={{ boxShadow: "outline" }}
        aria-label={String(
          t("{{month}}: {{count}} contracts expire", {
            month: monthStart.format("MMMM YYYY"),
            count: total,
          }),
        )}
        data-testid={`expiry-month-${month.month}`}
        {...rest}
      >
        <Text fontSize="xs" color="smText" textAlign="center">
          {total > 0 ? formatNumber(total) : ""}
        </Text>
        <Box
          h={height(month.withOption)}
          bg="smChartSeries1"
          borderTopRadius={month.withoutOption ? 0 : "sm"}
        />
        <Box h={height(month.withoutOption)} bg="smChartSeries2" />
        <Text
          fontSize={compact ? "2xs" : "xs"}
          color="smMuted"
          textAlign="center"
          whiteSpace="nowrap"
          mt="1"
          data-testid={`expiry-month-label-${month.month}`}
        >
          {label}
        </Text>
        {compact && (
          <Text
            fontSize="2xs"
            color="smMuted"
            textAlign="left"
            whiteSpace="nowrap"
            h="4"
            data-testid={`expiry-month-year-${month.month}`}
          >
            {yearLabel}
          </Text>
        )}
      </Flex>
    );
  },
);

const MonthDetails = ({
  month,
  currency,
  link,
}: {
  month: ContractExpiryMonthDto;
  currency: string;
  link: string;
}) => {
  const { t } = useTranslation();
  const total = month.withOption + month.withoutOption;
  const monthName = useMonthStart(month).format("MMMM YYYY");
  return (
    <Stack spacing="1">
      <Text fontWeight="semibold" color="smText">
        {t("{{count}} contracts expire in {{month}}", {
          count: total,
          month: monthName,
        })}
      </Text>
      <Text color="smMuted">
        {t("Annual value: {{value}}", {
          value: displaySum({
            value: Math.round(month.annualValue),
            currency,
          }),
        })}
      </Text>
      <Text color="smMuted">
        {t("{{count}} of them have an option", {
          count: month.withOption,
        })}
      </Text>
      {month.withoutAnnualValue > 0 && (
        <Text color="smMuted">
          {t(
            "{{count}} without an annual value in {{currency}} are not in the value.",
            {
              count: month.withoutAnnualValue,
              currency,
            },
          )}
        </Text>
      )}
      <SeeTheListButton to={link} />
    </Stack>
  );
};
